<script setup lang="ts">
import type { BulkCaptureThumbnail } from "@/composables/useBulkCapture"
import { ICON } from "@/lib/icons"

defineProps<{
  groupNumber: number
  photoCount: number
  pendingCount: number
  thumbnails: BulkCaptureThumbnail[]
  error: string | null
  queueBlocked: boolean
}>()
</script>

<template>
  <div class="pointer-events-none flex flex-col gap-2 p-4">
    <div class="flex items-center justify-between gap-2">
      <div
        class="rounded-full bg-black/70 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm"
      >
        Item {{ groupNumber }} · {{ photoCount }} {{ photoCount === 1 ? "photo" : "photos" }}
      </div>
      <div
        v-if="pendingCount > 0"
        data-testid="pending-status"
        role="status"
        aria-live="polite"
        class="bg-warning text-inverted rounded-full px-4 py-2 text-xs font-semibold"
      >
        {{ pendingCount }} pending
      </div>
    </div>

    <div v-if="thumbnails.length" class="flex gap-2 overflow-hidden">
      <div
        v-for="thumbnail in thumbnails"
        :key="thumbnail.id"
        data-testid="bulk-thumbnail"
        class="relative size-14 shrink-0 overflow-hidden rounded-lg border border-white/30 bg-black/60"
      >
        <img
          v-if="thumbnail.url"
          :src="thumbnail.url"
          alt="Captured photo"
          class="size-full object-cover"
        />
        <UIcon v-else :name="ICON.image" class="absolute inset-4 size-6 text-white/60" />
        <div
          v-if="thumbnail.pending"
          class="absolute inset-x-0 bottom-0 bg-black/70 py-0.5 text-center text-[10px] text-white"
        >
          Uploading
        </div>
      </div>
    </div>

    <div
      v-if="error"
      data-testid="capture-error"
      role="alert"
      aria-live="assertive"
      class="border-error bg-error text-inverted rounded-xl border px-4 py-2 text-sm"
    >
      {{ error }}
    </div>
    <div
      v-else-if="queueBlocked"
      role="status"
      aria-live="polite"
      class="bg-warning text-inverted rounded-xl px-4 py-2 text-sm font-medium"
    >
      Storage is full. Existing photos are safe.
    </div>
  </div>
</template>
